import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Shield, 
  FileText, 
  Lock, 
  Mail, 
  ArrowLeft, 
  Eye, 
  UserCheck,
  AlertCircle,
  CheckCircle,
  Globe
} from 'lucide-react';

const TermsAndPrivacy = () => {
  const [activeTab, setActiveTab] = useState('terms');
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png" 
                  alt="ReqTrak Logo" 
                  className="h-20 w-20 object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-slate-800">ReqTrak Platform</h1>
                  <p className="text-xs text-slate-600">Legal & Privacy</p>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service & Privacy Policy</h2>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span>Effective: September 18, 2025</span>
            <span>•</span>
            <span>Contact: support@reqtrak.com</span>
            <span>•</span>
            <span>Privacy: privacy@reqtrak.com</span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Terms</span>
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Privacy</span>
            </TabsTrigger>
            <TabsTrigger value="cookies" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Cookies</span>
            </TabsTrigger>
            <TabsTrigger value="rights" className="flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Rights</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Contact</span>
            </TabsTrigger>
          </TabsList>

          {/* Terms of Service */}
          <TabsContent value="terms" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Terms of Service
                </CardTitle>
                <CardDescription>
                  Legal terms governing your use of the ReqTrak platform
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed">
                    Welcome to ReqTrak.com ("ReqTrak," "we," "our," or "us"). These Terms of Service ("Terms") 
                    govern your access to and use of our website, applications, and related services (collectively, 
                    the "Services"). By accessing or using the Services, you agree to be bound by these Terms.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">1. Acceptance of Terms</h3>
                  <p className="text-slate-700 leading-relaxed">
                    By creating an account or using ReqTrak services, you acknowledge that you have read, 
                    understood, and agree to be bound by these Terms and our Privacy Policy.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2. Description of Service</h3>
                  <p className="text-slate-700 leading-relaxed">
                    ReqTrak is a public records request management platform that enables citizens to submit 
                    requests to police departments and allows departments to manage, track, and respond to 
                    these requests efficiently.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">3. User Accounts and Responsibilities</h3>
                  <ul className="list-disc list-inside text-slate-700 space-y-2">
                    <li>You must provide accurate and complete information when creating an account</li>
                    <li>You are responsible for maintaining the security of your account credentials</li>
                    <li>You must notify us immediately of any unauthorized use of your account</li>
                    <li>You are responsible for all activities that occur under your account</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">4. Acceptable Use</h3>
                  <p className="text-slate-700 leading-relaxed">
                    You agree not to use the Services for any unlawful purpose or in any way that could damage, 
                    disable, overburden, or impair our servers or networks. You may not attempt to gain 
                    unauthorized access to any part of the Services.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">5. Public Records Requests</h3>
                  <p className="text-slate-700 leading-relaxed">
                    When submitting public records requests, you acknowledge that such requests are subject to 
                    applicable federal, state, and local laws. Departments reserve the right to deny requests 
                    that do not comply with applicable legal requirements.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6. Limitations of Liability</h3>
                  <p className="text-slate-700 leading-relaxed">
                    ReqTrak provides the platform as-is. We do not guarantee the availability, accuracy, or 
                    completeness of the Services. We are not liable for any indirect, incidental, or 
                    consequential damages arising from your use of the Services.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7. Modifications to Terms</h3>
                  <p className="text-slate-700 leading-relaxed">
                    We reserve the right to modify these Terms at any time. We will notify users of significant 
                    changes via email or through the platform. Continued use after changes constitutes acceptance 
                    of the modified Terms.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">8. Termination</h3>
                  <p className="text-slate-700 leading-relaxed">
                    We may terminate or suspend your account at any time for violations of these Terms. 
                    You may terminate your account at any time by contacting us at support@reqtrak.com.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Policy */}
          <TabsContent value="privacy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Privacy Policy
                </CardTitle>
                <CardDescription>
                  How we collect, use, and protect your personal information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed">
                    This Privacy Policy explains how ReqTrak collects, uses, discloses, and safeguards 
                    information in connection with our Services.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Information We Collect</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-slate-800">Personal Information</h4>
                      <ul className="list-disc list-inside text-slate-700 mt-2 space-y-1">
                        <li>Name, email address, and contact information</li>
                        <li>Account credentials and authentication data</li>
                        <li>Information provided in public records requests</li>
                        <li>Communication records with support or department staff</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-slate-800">Technical Information</h4>
                      <ul className="list-disc list-inside text-slate-700 mt-2 space-y-1">
                        <li>IP address, browser type, and device information</li>
                        <li>Usage data and interaction patterns</li>
                        <li>Log files and error reports</li>
                        <li>Cookies and similar tracking technologies</li>
                      </ul>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">How We Use Your Information</h3>
                  <ul className="list-disc list-inside text-slate-700 space-y-2">
                    <li>To provide and maintain the ReqTrak services</li>
                    <li>To process and manage public records requests</li>
                    <li>To communicate with you about your account and requests</li>
                    <li>To improve our services and user experience</li>
                    <li>To ensure security and prevent fraud</li>
                    <li>To comply with legal obligations</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Information Sharing</h3>
                  <p className="text-slate-700 leading-relaxed">
                    We do not sell, trade, or rent personal information to third parties. We may share 
                    information in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 mt-3 space-y-2">
                    <li>With police departments to process your public records requests</li>
                    <li>With service providers who assist in operating our platform</li>
                    <li>When required by law or to protect our rights</li>
                    <li>In connection with a business transfer or merger</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Data Retention</h3>
                  <p className="text-slate-700 leading-relaxed">
                    We retain personal information for as long as necessary to provide services, comply with 
                    legal obligations, and resolve disputes. Account information may be retained for up to 
                    7 years after account closure for legal and audit purposes.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">International Transfers</h3>
                  <p className="text-slate-700 leading-relaxed">
                    Your information may be transferred to and processed in countries other than your own. 
                    We ensure appropriate safeguards are in place to protect your personal information during 
                    such transfers.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cookies & Tracking */}
          <TabsContent value="cookies" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Cookies & Tracking
                </CardTitle>
                <CardDescription>
                  How we use cookies and tracking technologies
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed">
                    We use essential cookies for login/session security and functional operation, and may use 
                    analytics cookies to understand usage and improve performance. You can manage cookies in 
                    your browser settings; disabling some cookies may impact functionality.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Types of Cookies We Use</h3>
                  
                  <div className="grid gap-4">
                    <div className="border border-green-200 bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <Badge className="bg-green-100 text-green-800">Essential</Badge>
                      </div>
                      <h4 className="font-medium text-green-800">Strictly Necessary Cookies</h4>
                      <p className="text-green-700 text-sm mt-1">
                        Required for login, authentication, and core platform functionality. 
                        Cannot be disabled without affecting site operation.
                      </p>
                    </div>

                    <div className="border border-blue-200 bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-blue-600" />
                        <Badge className="bg-blue-100 text-blue-800">Functional</Badge>
                      </div>
                      <h4 className="font-medium text-blue-800">Performance & Analytics Cookies</h4>
                      <p className="text-blue-700 text-sm mt-1">
                        Help us understand how users interact with the platform to improve 
                        performance and user experience. These can be disabled.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Managing Your Cookie Preferences</h3>
                  <ul className="list-disc list-inside text-slate-700 space-y-2">
                    <li>Most browsers allow you to control cookies through settings</li>
                    <li>You can delete existing cookies and prevent future cookies</li>
                    <li>Disabling essential cookies may prevent proper platform functionality</li>
                    <li>Some third-party tools also provide cookie management options</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Third-Party Services</h3>
                  <p className="text-slate-700 leading-relaxed">
                    We may use third-party services for analytics, error monitoring, and performance 
                    optimization. These services may set their own cookies subject to their privacy policies.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Your Privacy Rights */}
          <TabsContent value="rights" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Your Privacy Rights
                </CardTitle>
                <CardDescription>
                  Rights you have regarding your personal information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed">
                    Depending on your location (e.g., GDPR in the EU/UK, CCPA/CPRA in California), 
                    you may have rights to:
                  </p>

                  <div className="grid gap-4 mt-6">
                    <div className="border border-slate-200 p-4 rounded-lg">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Right to Access
                      </h4>
                      <p className="text-slate-700 text-sm mt-2">
                        Request access to the personal information we hold about you and how we process it.
                      </p>
                    </div>

                    <div className="border border-slate-200 p-4 rounded-lg">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Right to Rectification  
                      </h4>
                      <p className="text-slate-700 text-sm mt-2">
                        Request correction of inaccurate or incomplete personal information.
                      </p>
                    </div>

                    <div className="border border-slate-200 p-4 rounded-lg">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Right to Data Portability
                      </h4>
                      <p className="text-slate-700 text-sm mt-2">
                        Receive a portable copy of certain data you have provided to us.
                      </p>
                    </div>

                    <div className="border border-slate-200 p-4 rounded-lg">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Right to Object or Limit
                      </h4>
                      <p className="text-slate-700 text-sm mt-2">
                        Object to or limit certain processing of your personal information.
                      </p>
                    </div>

                    <div className="border border-red-200 bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-800 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Right to Erasure
                      </h4>
                      <p className="text-red-700 text-sm mt-2">
                        Request deletion of your personal information, subject to legal retention requirements.
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-6">
                    <h4 className="font-medium text-blue-800 mb-2">Important Note</h4>
                    <p className="text-blue-700 text-sm">
                      We do not sell personal information to third parties. If you're a California resident, 
                      you have the right to opt-out of the sale or sharing of personal information, though 
                      this right may not apply to ReqTrak as we don't engage in such practices.
                    </p>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">How to Exercise Your Rights</h3>
                  <p className="text-slate-700 leading-relaxed">
                    To exercise any of these rights, please contact us at privacy@reqtrak.com. 
                    We will respond to your request within the timeframes required by applicable law, 
                    typically within 30 days.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security
                </CardTitle>
                <CardDescription>
                  How we protect your information and platform security
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed">
                    We implement administrative, technical, and physical safeguards—such as encryption in 
                    transit, access controls, logging, and secure development practices—to protect information. 
                    No system is perfectly secure; you are responsible for maintaining the security of your 
                    account and promptly notifying us of suspected compromise.
                  </p>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Our Security Measures</h3>
                  
                  <div className="grid gap-4">
                    <div className="border border-green-200 bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-800 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Encryption
                      </h4>
                      <p className="text-green-700 text-sm mt-2">
                        All data transmission is encrypted using industry-standard TLS encryption. 
                        Passwords are securely hashed using modern cryptographic algorithms.
                      </p>
                    </div>

                    <div className="border border-blue-200 bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-800 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Access Controls
                      </h4>
                      <p className="text-blue-700 text-sm mt-2">
                        Strict role-based access controls ensure users only access information 
                        appropriate to their role and department.
                      </p>
                    </div>

                    <div className="border border-purple-200 bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-800 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Monitoring & Logging
                      </h4>
                      <p className="text-purple-700 text-sm mt-2">
                        Comprehensive logging and monitoring systems help detect and respond to 
                        security incidents quickly.
                      </p>
                    </div>

                    <div className="border border-orange-200 bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-800 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Regular Security Assessments
                      </h4>
                      <p className="text-orange-700 text-sm mt-2">
                        We conduct regular security reviews, penetration testing, and vulnerability 
                        assessments to identify and address potential risks.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Your Security Responsibilities</h3>
                  <ul className="list-disc list-inside text-slate-700 space-y-2">
                    <li>Use strong, unique passwords for your ReqTrak account</li>
                    <li>Enable two-factor authentication when available</li>
                    <li>Keep your contact information up to date</li>
                    <li>Log out of your account when using shared devices</li>
                    <li>Report suspicious activity immediately to support@reqtrak.com</li>
                    <li>Do not share your account credentials with others</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">Incident Response</h3>
                  <p className="text-slate-700 leading-relaxed">
                    In the event of a security incident affecting personal information, we will notify 
                    affected users and relevant authorities as required by law, typically within 72 hours 
                    of discovery.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact */}
          <TabsContent value="contact" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contact Information
                </CardTitle>
                <CardDescription>
                  How to reach us for questions about these Terms or Privacy practices
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="border border-blue-200 bg-blue-50 p-6 rounded-lg">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-3">
                      <Mail className="w-5 h-5" />
                      General Support
                    </h4>
                    <p className="text-blue-700 text-sm mb-3">
                      For questions about your account, technical issues, or general inquiries.
                    </p>
                    <div className="space-y-2">
                      <p className="text-blue-800 font-medium">support@reqtrak.com</p>
                      <p className="text-blue-700 text-xs">
                        Response time: 1-2 business days
                      </p>
                    </div>
                  </div>

                  <div className="border border-purple-200 bg-purple-50 p-6 rounded-lg">
                    <h4 className="font-medium text-purple-800 flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5" />
                      Privacy Requests
                    </h4>
                    <p className="text-purple-700 text-sm mb-3">
                      For privacy-related questions, data requests, or exercising your privacy rights.
                    </p>
                    <div className="space-y-2">
                      <p className="text-purple-800 font-medium">privacy@reqtrak.com</p>
                      <p className="text-purple-700 text-xs">
                        Response time: Within 30 days as required by law
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-6 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-3">When Contacting Us</h4>
                  <p className="text-slate-700 text-sm mb-3">
                    To help us assist you more effectively, please include:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                    <li>Your registered email address</li>
                    <li>A clear description of your question or request</li>
                    <li>Any relevant account or request numbers</li>
                    <li>Screenshots or error messages if applicable</li>
                  </ul>
                </div>

                <div className="bg-slate-100 p-6 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-2">Business Information</h4>
                  <div className="text-slate-700 text-sm space-y-1">
                    <p><strong>ReqTrak Platform</strong></p>
                    <p>Public Records Request Management System</p>
                    <p>Website: <a href="/" className="text-blue-600 hover:underline">reqtrak.com</a></p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <div className="text-center text-sm text-slate-500">
            <p>© 2025 iDeare Inc. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <button 
                onClick={() => setActiveTab('terms')}
                className="text-blue-600 hover:underline"
              >
                Terms
              </button>
              <span>•</span>
              <button 
                onClick={() => setActiveTab('privacy')}
                className="text-blue-600 hover:underline"
              >
                Privacy
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsAndPrivacy;